/**
 * Checks window.initialState against what the frontend reads from it (the types in
 * initial-state.ts). It only reports: the app renders as before, and app.ts sends the problems
 * to Sentry, so a backend change is noticed before it breaks a page. Pages pass only the keys
 * they need (success and not-found pass none), so a key is checked only when it is present.
 *
 * Problems name the field and the types, never the value — initialState holds the user's email.
 */

type Kind = 'string' | 'number' | 'boolean' | 'array' | 'object';

const PLAN_FIELDS: Record<string, Kind> = {
  'item-id': 'number',
  'item-title': 'string',
  'item-package': 'string',
  'item-currency-code': 'string',
  'item-currency-code-usd': 'string',
  'item-best-value-price': 'boolean',
  'item-price': 'number',
  'item-price-usd': 'number',
  'item-price-regular': 'number',
  'item-device-count': 'number',
  'item-period-type': 'number',
  'item-period-count': 'number',
  'item-recurring-price': 'number',
  'item-recurring-count': 'number',
  'item-offer-status': 'number',
  'item-offer-price': 'number',
  'item-offer-price-usd': 'number',
  'item-offer-period-type': 'number',
  'item-offer-period-count': 'number',
  'item-extra-status': 'number',
  'item-extra-period-type': 'number',
  'item-extra-period-count': 'number',
  'item-gateways': 'array',
  'item-paddle-prices': 'object',
};

const GATEWAY_FIELDS: Record<string, Kind> = {
  'item-id': 'number',
  'item-active': 'boolean',
  'item-label': 'string',
  'item-data': 'object',
};

const TOP_FIELDS: Record<string, Kind> = {
  email: 'string',
  temp_email: 'string',
  page_item: 'string',
  plans: 'array',
  gateways: 'array',
  paddleResume: 'object',
};

function kindOf(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';

  return typeof value;
}

function check(value: unknown, kind: Kind, path: string, problems: string[]) {
  const actual = kindOf(value);

  if (actual === 'undefined') {
    problems.push(`${path}: missing`);
    return false;
  }

  if (actual !== kind) {
    problems.push(`${path}: expected ${kind}, got ${actual}`);
    return false;
  }

  return true;
}

function checkFields(
  item: unknown,
  fields: Record<string, Kind>,
  path: string,
  problems: string[],
) {
  if (!check(item, 'object', path, problems)) return;

  const record = item as Record<string, unknown>;

  Object.entries(fields).forEach(([field, kind]) => {
    check(record[field], kind, `${path}.${field}`, problems);
  });
}

export function validateInitialState(state: unknown): string[] {
  const problems: string[] = [];

  if (!check(state, 'object', 'initialState', problems)) {
    return problems;
  }

  const record = state as Record<string, unknown>;

  Object.entries(TOP_FIELDS).forEach(([field, kind]) => {
    if (field in record) {
      check(record[field], kind, field, problems);
    }
  });

  if (Array.isArray(record.plans)) {
    if (record.plans.length === 0) {
      problems.push('plans: empty');
    }

    record.plans.forEach((plan, i) => {
      checkFields(plan, PLAN_FIELDS, `plans[${i}]`, problems);

      const gateways = (plan as Record<string, unknown> | null)?.[
        'item-gateways'
      ];

      if (Array.isArray(gateways)) {
        gateways.forEach((id, j) => {
          check(id, 'number', `plans[${i}].item-gateways[${j}]`, problems);
        });
      }
    });
  }

  if (Array.isArray(record.gateways)) {
    record.gateways.forEach((gateway, i) => {
      checkFields(gateway, GATEWAY_FIELDS, `gateways[${i}]`, problems);
    });
  }

  return problems;
}
